import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { router } from "@inertiajs/react"
import { useState } from "react"
import type { DocumentDetail, TreeNode } from "../types"

const TOP_LEVEL = "top-level"

function descendantIds(nodes: TreeNode[], id: number) {
  const found = new Set<number>()

  const walk = (parentId: number) => {
    for (const node of nodes.filter((candidate) => candidate.parent_id === parentId)) {
      found.add(node.id)
      walk(node.id)
    }
  }

  walk(id)
  return found
}

interface MoveDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  document: DocumentDetail
  nodes: TreeNode[]
}

export function MoveDialog({ open, onOpenChange, document: doc, nodes }: MoveDialogProps) {
  const [target, setTarget] = useState(TOP_LEVEL)
  const excluded = descendantIds(nodes, doc.id)

  const targets = nodes.filter((node) => node.section === "private" && node.id !== doc.id && !excluded.has(node.id))

  const move = () => {
    router.patch(
      `/documents/${doc.id}/move`,
      { parent_id: target === TOP_LEVEL ? null : Number(target) },
      { preserveScroll: true, onSuccess: () => onOpenChange(false) },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Move page</DialogTitle>
          <DialogDescription>
            A moved page takes its own nested pages with it, and picks up the access of wherever it lands.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="move_target">Move under</Label>
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger id="move_target">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TOP_LEVEL}>Top level</SelectItem>
              {targets.map((node) => (
                <SelectItem key={node.id} value={String(node.id)}>
                  {node.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={move}>Move</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
